import React, { memo, useState, useEffect, useCallback } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';
import { UserProfileWrapper } from 'Components/Profiles';

import classes from './apiTokens.module.css';

interface Token {
  id: number;
  name: string;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function CopyTokenBanner({ plainToken, onDismiss }: { plainToken: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(() => {
    navigator.clipboard.writeText(plainToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [plainToken]);

  return (
    <div className={classes.tokenBanner}>
      <p className={classes.tokenBannerTitle}>Token created — copy it now. It will not be shown again.</p>
      <div className={classes.tokenRow}>
        <code className={classes.tokenValue}>{plainToken}</code>
        <button type="button" className={classes.copyButton} onClick={onCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <button type="button" className={classes.dismissButton} onClick={onDismiss}>
        I've saved my token
      </button>
    </div>
  );
}

function CreateTokenForm({ onCreate }: { onCreate: (name: string) => Promise<void> }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Token name is required.');
        return;
      }
      setError('');
      setLoading(true);
      try {
        await onCreate(trimmed);
        setName('');
      } catch {
        setError('Failed to create token. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [name, onCreate]
  );

  return (
    <form className={classes.createForm} onSubmit={onSubmit}>
      <h3 className={classes.subheading}>Create New Token</h3>
      <div className={classes.createRow}>
        <input
          className={classes.nameInput}
          type="text"
          placeholder="Token name (e.g. My Integration)"
          value={name}
          maxLength={255}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className={classes.createButton}
          disabled={loading || !name.trim()}
        >
          {loading ? 'Creating...' : 'Create Token'}
        </button>
      </div>
      {error && <p className={classes.errorText}>{error}</p>}
    </form>
  );
}

function TokenRow({ token, onRevoke }: { token: Token; onRevoke: (id: number) => void }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <tr className={classes.tableRow}>
      <td className={classes.tableCell}>{token.name}</td>
      <td className={classes.tableCell}>{formatDate(token.created_at)}</td>
      <td className={classes.tableCell}>{formatDate(token.last_used_at)}</td>
      <td className={`${classes.tableCell} ${classes.revokeCell}`}>
        {confirming ? (
          <span className={classes.confirmRow}>
            <span className={classes.confirmText}>Revoke?</span>
            <button type="button" className={classes.revokeConfirmButton} onClick={() => onRevoke(token.id)}>
              Yes
            </button>
            <button type="button" className={classes.cancelRevokeButton} onClick={() => setConfirming(false)}>
              No
            </button>
          </span>
        ) : (
          <button type="button" className={classes.revokeButton} onClick={() => setConfirming(true)}>
            Revoke
          </button>
        )}
      </td>
    </tr>
  );
}

function ApiTokensContent() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPlainToken, setNewPlainToken] = useState<string | null>(null);

  const fetchTokens = useCallback(async () => {
    try {
      const { data } = await Api.get('/auth/user/tokens');
      setTokens(data);
    } catch {
      // silently fail — tokens list stays empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, []);

  const onCreate = useCallback(async (name: string) => {
    const { data } = await Api.post('/auth/user/tokens', { name });
    setNewPlainToken(data.token);
    setTokens((prev) => [{ id: data.id, name: data.name, created_at: data.created_at, last_used_at: null, expires_at: null }, ...prev]);
  }, []);

  const onRevoke = useCallback(async (id: number) => {
    try {
      await Api.delete(`/auth/user/tokens/${id}`);
      setTokens((prev) => prev.filter((t) => t.id !== id));
    } catch {
      // silently fail
    }
  }, []);

  const onDismissBanner = useCallback(() => setNewPlainToken(null), []);

  return (
    <div className={classes.container}>
      <h2 className={classes.heading}>API Tokens</h2>
      <p className={classes.description}>
        Personal API tokens let you authenticate with the RocketPlan API from scripts and integrations.
      </p>

      {newPlainToken && <CopyTokenBanner plainToken={newPlainToken} onDismiss={onDismissBanner} />}

      {loading ? (
        <p className={classes.emptyText}>Loading tokens...</p>
      ) : tokens.length === 0 ? (
        <p className={classes.emptyText}>No tokens yet. Create one below.</p>
      ) : (
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.tableHeader}>Name</th>
              <th className={classes.tableHeader}>Created</th>
              <th className={classes.tableHeader}>Last Used</th>
              <th className={classes.tableHeader} />
            </tr>
          </thead>
          <tbody>
            {tokens.map((token) => (
              <TokenRow key={token.id} token={token} onRevoke={onRevoke} />
            ))}
          </tbody>
        </table>
      )}

      <CreateTokenForm onCreate={onCreate} />
    </div>
  );
}

function UserApiTokensContainer() {
  return (
    <UserProfileWrapper tab="api-tokens">
      <ApiTokensContent />
    </UserProfileWrapper>
  );
}

const UserApiTokensContainerMemo = memo(UserApiTokensContainer, areEqual);

export { UserApiTokensContainerMemo as UserApiTokens };
